import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Email } from '@kula-ai/components';
import type { EmailCompositionValue } from '@kula-ai/components';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { applyTemplateMetadataOnChange } from '@/components/shared/interview-manager/InterviewActivity/communication/helpers/templateApply';
import {
  SurveyNode,
  SurveyLinkReplacement,
} from '@/components/formBuilder/formFields/FormEditor/extensions';
import {
  toEmailValue,
  fromEmailValue,
  isSameEmailValue,
  toEditorAttachments,
  attachmentKey,
} from './rejectEmailValue';
import type {
  RejectEmailConfig,
  RejectFromUser,
  RejectAttachment,
} from './rejectEmailValue';

interface SurveySelection {
  value?: string | number;
  label?: string;
}

interface RejectTemplate {
  id: string | number;
  name?: string;
  body?: string;
  subject?: string;
  attachments?: unknown;
  // kula's `SelectOption.data` is an open record; templates carry more fields
  // than we name here and the send payload still reads them.
  [key: string]: unknown;
}

interface RejectEmailComposerProps {
  config: RejectEmailConfig;
  candidateEmail?: string;
  templates?: RejectTemplate[];
  onChange: (_config: RejectEmailConfig) => void;
  onTemplateChange?: (_template: RejectTemplate | null) => void;
  attachments?: RejectAttachment[];
  onAttachmentsChange?: (_items: RejectAttachment[]) => void;
  selectedSurvey?: SurveySelection | null;
  onSurveySelect?: (_survey: SurveySelection) => void;
  onSurveyRemove?: () => void;
  submitAttempt?: number;
}

// Rejection emails ship as real HTML, so offer the template editor's typography.
const TOOLBAR_FEATURES = {
  image: true,
  attachment: true,
  fontSize: true,
  fontFamily: true,
} as const;

// `value` stays the email — the send path resolves the sender by matching it.
const toSelectOptions = (users: RejectFromUser[] = []) =>
  users
    .filter((user) => user?.email)
    .map((user) => ({
      value: user.email as string,
      label: user.name || (user.email as string),
      description: user.name ? user.email : undefined,
      image: user.avatar ?? undefined,
    }));

/**
 * Reject-email composition on kula-ai `Email`, which owns the template picker.
 *
 * The legacy `FormEditor` disabled tiptap's `hardBreak`, so it dropped every
 * `<br>` in a stored template body (ENG-3719).
 */
export const RejectEmailComposer = ({
  config,
  candidateEmail,
  templates = [],
  onChange,
  onTemplateChange,
  attachments = [],
  onAttachmentsChange,
  selectedSurvey = null,
  onSurveySelect,
  onSurveyRemove,
  submitAttempt = 0,
}: RejectEmailComposerProps) => {
  const { fromUsers, allUsers } = useGetAllUsers();

  // `description` carries the survey badge the removed select used to render.
  const templateOptions = useMemo(
    () =>
      templates.map((template) => ({
        label: template.name ?? '',
        value: String(template.id),
        description: String(template.body ?? '').includes('{{survey.link}}')
          ? 'Has survey'
          : undefined,
        data: template,
      })),
    [templates]
  );

  const fromOptions = useMemo(() => toSelectOptions(fromUsers), [fromUsers]);
  const ccBccOptions = useMemo(() => toSelectOptions(allUsers), [allUsers]);

  // Shared mailboxes need the whole option object — it carries `isSharedEmail`.
  const resolveFromUser = useCallback(
    (from: string) => fromUsers?.find((user) => user?.email === from),
    [fromUsers]
  );

  // `config` is a seed only — binding `value` to it would spin the form.
  const [value, setValue] = useState(() =>
    toEmailValue(config, candidateEmail)
  );
  const valueRef = useRef(value);

  // `Email` re-emits on identity change; storing no-op emits would loop.
  const handleChange = useCallback(
    (next: EmailCompositionValue) => {
      const prev = valueRef.current;
      // kula applies the picked template's body; this merges its subject too.
      const merged = applyTemplateMetadataOnChange(prev, next, templateOptions);
      if (isSameEmailValue(prev, merged)) return;

      valueRef.current = merged;
      setValue(merged);
      onChange(fromEmailValue(merged, resolveFromUser));

      if (merged.template !== prev.template) {
        const picked = templateOptions.find((o) => o.value === merged.template);
        onTemplateChange?.(picked?.data ?? null);
      }
    },
    [onChange, onTemplateChange, resolveFromUser, templateOptions]
  );

  // Adopt the default sender only if it is selectable — an unmatched address is
  // truthy, so it passes both validators and only fails at the backend.
  // One-shot: this writes to Formik, and re-entering would lock the browser.
  const seededFrom = useRef(false);
  useEffect(() => {
    if (seededFrom.current || !fromOptions.length) return;
    seededFrom.current = true;

    const wanted = config?.from;
    if (!wanted || !fromOptions.some((option) => option.value === wanted)) {
      return;
    }
    const seeded = { ...valueRef.current, from: wanted };
    valueRef.current = seeded;
    setValue(seeded);
    onChange(fromEmailValue(seeded, resolveFromUser));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromOptions]);

  // Formik holds a mixed File / template-row array; map to chips and back by key.
  const editorAttachments = useMemo(
    () => toEditorAttachments(attachments),
    [attachments]
  );

  const attachmentHandlers = useMemo(
    () =>
      onAttachmentsChange
        ? {
            attachments: editorAttachments,
            onAttachFiles: (files: File[]) =>
              onAttachmentsChange([...attachments, ...files]),
            onRemoveAttachment: (key: string) =>
              onAttachmentsChange(
                attachments.filter((item, i) => attachmentKey(item, i) !== key)
              ),
          }
        : { attachments: editorAttachments },
    [attachments, editorAttachments, onAttachmentsChange]
  );

  // Repo-local tiptap nodes; kula peer-deps tiptap so they share one instance.
  const extraExtensions = useMemo(
    () => [
      SurveyNode.configure({ onSurveySelect, onSurveyRemove }),
      SurveyLinkReplacement.configure({
        initialSurveyId: selectedSurvey?.value ?? null,
      }),
    ],
    [onSurveySelect, onSurveyRemove, selectedSurvey?.value]
  );

  return (
    <Email
      showFrom
      showTo={false}
      showSubject
      showTemplate
      showEditor
      enableCcBcc
      applyTemplateBodyToEditor
      value={value}
      onChange={handleChange}
      fromOptions={fromOptions}
      ccBccOptions={ccBccOptions}
      templateOptions={templateOptions}
      submitAttempt={submitAttempt}
      placeholders={{
        from: 'Select from',
        subject: 'Enter subject',
        template: 'Select email template',
      }}
      editorProps={{
        placeholder: 'Start typing...',
        toolbarFeatures: TOOLBAR_FEATURES,
        extraExtensions,
        ...attachmentHandlers,
      }}
    />
  );
};
